import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button, Skeleton, Fade, Snackbar, Alert
} from '@mui/material';
import { ChevronLeft, ChevronRight, Add, Today, EventNote, Delete } from '@mui/icons-material';
import dayjs from 'dayjs';
import { api } from '../api';
import UsualClasses from '../components/UsualClasses';

const EVENT_COLORS = [
  { bg: '#EEF4FF', text: '#3538CD', border: '#C7D7FE' },
  { bg: '#FDF2FA', text: '#C11574', border: '#FCCEEE' },
  { bg: '#ECFDF3', text: '#067647', border: '#ABEFC6' },
  { bg: '#FFFAEB', text: '#B54708', border: '#FEDF89' },
  { bg: '#F4F3FF', text: '#5925DC', border: '#D9D6FE' },
  { bg: '#FFF1F3', text: '#C01048', border: '#FECDD6' },
];

function getColorForClass(className) {
  let hash = 0;
  for (let i = 0; i < className.length; i++) {
    hash = className.charCodeAt(i) + ((hash << 5) - hash);
  }
  return EVENT_COLORS[Math.abs(hash) % EVENT_COLORS.length];
}

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [sessions, setSessions] = useState([]);
  const [scheduledEvents, setScheduledEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  const handleDeleteSuggested = async (e, id) => {
    e.stopPropagation();
    try {
      await api.deleteScheduledEvent(id);
      setScheduledEvents(prev => prev.filter(ev => ev.id !== id));
      setSnackbar({ open: true, message: 'Event removed', severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const start = currentMonth.startOf('month').subtract(7, 'day').format('YYYY-MM-DD');
      const end = currentMonth.endOf('month').add(7, 'day').format('YYYY-MM-DD');
      const [sessData, schedData] = await Promise.all([
        api.getSessions(start, end),
        api.getScheduledEvents(start, end),
      ]);
      setSessions(sessData);
      setScheduledEvents(schedData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const today = dayjs().format('YYYY-MM-DD');

  // ?asOf=YYYY-MM-DD previews the usual-classes panel for another day (e.g. next Sunday)
  const [searchParams] = useSearchParams();
  const asOf = searchParams.get('asOf');
  const usualDate = asOf && /^\d{4}-\d{2}-\d{2}$/.test(asOf) && dayjs(asOf).isValid() ? asOf : today;

  // Classes already marked on that day; kept when browsing other months so the panel doesn't blank out
  const [usualDone, setUsualDone] = useState([]);
  useEffect(() => {
    if (loading || !currentMonth.isSame(dayjs(usualDate), 'month')) return;
    setUsualDone(sessions
      .filter(s => (s.session_date || s.sessionDate) === usualDate)
      .map(s => s.classId || s.class_id));
  }, [loading, sessions, currentMonth, usualDate]);

  const sessionsByDate = {};
  sessions.forEach(s => {
    const date = s.session_date || s.sessionDate;
    if (!date) return;
    if (!sessionsByDate[date]) sessionsByDate[date] = [];
    sessionsByDate[date].push(s);
  });

  const scheduledByDate = {};
  scheduledEvents.forEach(e => {
    const date = e.event_date || e.eventDate;
    if (!date) return;
    if (!scheduledByDate[date]) scheduledByDate[date] = [];
    scheduledByDate[date].push(e);
  });

  // Build calendar grid including prev/next month overflow days
  const firstOfMonth = currentMonth.startOf('month');
  const lastOfMonth = currentMonth.endOf('month');
  const startDay = firstOfMonth.day(); // 0=Sun
  const daysInMonth = currentMonth.daysInMonth();
  const prevMonth = currentMonth.subtract(1, 'month');
  const prevMonthDays = prevMonth.daysInMonth();

  const calendarDays = [];
  // Previous month overflow
  for (let i = startDay - 1; i >= 0; i--) {
    const d = prevMonthDays - i;
    calendarDays.push({ day: d, currentMonth: false, date: prevMonth.date(d).format('YYYY-MM-DD') });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push({ day: d, currentMonth: true, date: currentMonth.date(d).format('YYYY-MM-DD') });
  }
  // Next month overflow — fill to complete the last row only
  const nextMonth = currentMonth.add(1, 'month');
  const remaining = (7 - (calendarDays.length % 7)) % 7;
  for (let d = 1; d <= remaining; d++) {
    calendarDays.push({ day: d, currentMonth: false, date: nextMonth.date(d).format('YYYY-MM-DD') });
  }

  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  const selectedSessions = sessionsByDate[selectedDate] || [];
  const selectedScheduled = scheduledByDate[selectedDate] || [];
  // Scheduled events that don't already have attendance recorded
  const suggestedClasses = selectedScheduled.filter(
    se => !selectedSessions.some(s => (s.classId || s.class_id) === (se.classId || se.class_id))
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 680, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#101828', letterSpacing: '-0.02em' }}>
            {currentMonth.format('MMMM YYYY')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085', mt: 0.25 }}>
            {selectedDate === today ? 'Today' : dayjs(selectedDate).format('dddd, MMMM D')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Button
            size="small" variant="outlined"
            onClick={() => { setCurrentMonth(dayjs()); setSelectedDate(today); }}
            sx={{
              minWidth: 'auto', px: 1.5, borderColor: '#D0D5DD', color: '#344054',
              fontWeight: 600, fontSize: '0.8rem', borderRadius: 2,
              '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' }
            }}
          >
            Today
          </Button>
          <IconButton
            size="small"
            onClick={() => setCurrentMonth(m => m.subtract(1, 'month'))}
            sx={{ border: '1px solid #D0D5DD', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#F9FAFB' } }}
          >
            <ChevronLeft sx={{ fontSize: 20, color: '#344054' }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setCurrentMonth(m => m.add(1, 'month'))}
            sx={{ border: '1px solid #D0D5DD', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#F9FAFB' } }}
          >
            <ChevronRight sx={{ fontSize: 20, color: '#344054' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Usual classes for the day, predicted from attendance history */}
      <UsualClasses date={usualDate} doneClassIds={usualDone} />

      {/* Calendar */}
      {loading ? (
        <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 3 }} />
      ) : (
        <Box sx={{
          border: '1px solid #EAECF0', borderRadius: 3, overflow: 'hidden',
          bgcolor: 'white', boxShadow: '0 1px 2px rgba(16,24,40,0.05)'
        }}>
          {/* Day headers */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            borderBottom: '1px solid #EAECF0', bgcolor: '#F9FAFB'
          }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <Box key={d} sx={{ py: 1.25, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {d}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Weeks */}
          {weeks.map((week, wi) => (
            <Box
              key={wi}
              sx={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                borderBottom: wi < weeks.length - 1 ? '1px solid #EAECF0' : 'none',
              }}
            >
              {week.map((cell, di) => {
                const isToday = cell.date === today;
                const isSelected = cell.date === selectedDate;
                const daySessions = sessionsByDate[cell.date] || [];
                const dayScheduled = scheduledByDate[cell.date] || [];
                const hasEvents = daySessions.length > 0 || dayScheduled.length > 0;

                return (
                  <Box
                    key={di}
                    onClick={() => setSelectedDate(cell.date)}
                    sx={{
                      height: { xs: 56, sm: 80 },
                      p: { xs: 0.5, sm: 0.75 },
                      cursor: 'pointer',
                      borderRight: di < 6 ? '1px solid #EAECF0' : 'none',
                      bgcolor: isSelected ? '#F9FAFB' : 'transparent',
                      transition: 'background 0.1s',
                      '&:hover': { bgcolor: '#F9FAFB' },
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Day number */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: isToday ? '#6366f1' : 'transparent',
                        border: isSelected && !isToday ? '2px solid #6366f1' : 'none',
                      }}>
                        <Typography sx={{
                          fontSize: '0.8rem',
                          fontWeight: isToday || isSelected ? 700 : 400,
                          color: isToday ? '#fff' : (cell.currentMonth ? '#101828' : '#D0D5DD'),
                        }}>
                          {cell.day}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Event pills */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                      {daySessions.slice(0, 2).map((session, si) => {
                        const color = getColorForClass(session.class_name);
                        const hasAbsent = session.absent_count > 0;
                        return (
                          <Box
                            key={`s-${si}`}
                            sx={{
                              bgcolor: hasAbsent ? '#FEF3F2' : color.bg,
                              borderLeft: `3px solid ${hasAbsent ? '#F04438' : color.border}`,
                              borderRadius: '0 4px 4px 0',
                              px: 0.5, py: 0.25,
                              overflow: 'hidden',
                            }}
                          >
                            <Typography sx={{
                              fontSize: { xs: '0.6rem', sm: '0.68rem' },
                              fontWeight: 600,
                              color: hasAbsent ? '#B42318' : color.text,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              lineHeight: 1.3,
                            }}>
                              {session.class_name}
                            </Typography>
                          </Box>
                        );
                      })}
                      {daySessions.length < 2 && dayScheduled
                        .filter(se => !daySessions.some(s => (s.classId || s.class_id) === (se.classId || se.class_id)))
                        .slice(0, 2 - daySessions.length)
                        .map((se, si) => (
                          <Box
                            key={`e-${si}`}
                            sx={{
                              bgcolor: '#F0F4FF',
                              borderLeft: '3px dashed #93A3D8',
                              borderRadius: '0 4px 4px 0',
                              px: 0.5, py: 0.25,
                              overflow: 'hidden',
                            }}
                          >
                            <Typography sx={{
                              fontSize: { xs: '0.6rem', sm: '0.68rem' },
                              fontWeight: 500,
                              fontStyle: 'italic',
                              color: '#5B6BA8',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              lineHeight: 1.3,
                            }}>
                              {se.class_name}
                            </Typography>
                          </Box>
                        ))}
                      {(daySessions.length + dayScheduled.length) > 2 && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#667085', fontWeight: 500, pl: 0.5 }}>
                          +{(daySessions.length + dayScheduled.length) - 2} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}

      {/* Selected day detail panel */}
      <Fade in={true} key={selectedDate}>
        <Box sx={{ mt: 2.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', columnGap: 1.5, rowGap: 1, mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#101828', whiteSpace: 'nowrap' }}>
              {dayjs(selectedDate).format('dddd, MMMM D')}
            </Typography>
            {/* On phones the buttons take their own full-width row; on wider screens they sit beside the date */}
            <Box sx={{ display: 'flex', gap: 0.75, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
              <Button
                size="small" variant="outlined" startIcon={<EventNote />}
                onClick={() => navigate('/schedule')}
                sx={{
                  flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap',
                  borderColor: '#93A3D8', color: '#5B6BA8', borderRadius: 2, fontSize: '0.8rem',
                  '&:hover': { borderColor: '#6366f1', color: '#4f46e5', bgcolor: '#F8F9FF' }
                }}
              >
                Schedule Class
              </Button>
              <Button
                size="small" variant="contained" startIcon={<Add />}
                onClick={() => navigate(`/attendance/${selectedDate}`)}
                sx={{
                  flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap',
                  bgcolor: '#6366f1', borderRadius: 2, fontSize: '0.8rem',
                  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
                  '&:hover': { bgcolor: '#4f46e5' }
                }}
              >
                Mark Attendance
              </Button>
            </Box>
          </Box>

          {/* Suggested Classes from scheduled events */}
          {suggestedClasses.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#5B6BA8', mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventNote sx={{ fontSize: 16 }} />
                Suggested Classes
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {suggestedClasses.map(se => (
                  <Box
                    key={se.id}
                    onClick={() => navigate(`/attendance/${selectedDate}?classId=${se.classId || se.class_id}`)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1.5, borderRadius: 2.5, cursor: 'pointer',
                      border: '1.5px dashed #93A3D8', bgcolor: '#F8F9FF',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#6366f1', bgcolor: '#F0F2FF', boxShadow: '0 2px 4px rgba(99,102,241,0.1)' }
                    }}
                  >
                    <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 2, bgcolor: '#93A3D8', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#3538CD' }}>
                        {se.class_name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#667085' }}>
                        {(se.start_time || se.startTime) ? `${se.start_time || se.startTime}${(se.end_time || se.endTime) ? ' - ' + (se.end_time || se.endTime) : ''}` : 'Tap to mark attendance'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteSuggested(e, se.id)}
                        sx={{ color: '#FDA29B', '&:hover': { color: '#F04438', bgcolor: '#FFF1F0' } }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                      <ChevronRight sx={{ color: '#93A3D8', fontSize: 20 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {selectedSessions.length === 0 && suggestedClasses.length === 0 ? (
            <Box sx={{
              border: '1px dashed #D0D5DD', borderRadius: 3, py: 4, textAlign: 'center',
              bgcolor: '#F9FAFB'
            }}>
              <Today sx={{ fontSize: 36, color: '#D0D5DD', mb: 1 }} />
              <Typography sx={{ color: '#667085', fontSize: '0.9rem' }}>
                No classes on this day
              </Typography>
              <Typography sx={{ color: '#98A2B3', fontSize: '0.8rem', mt: 0.5 }}>
                Tap "Mark Attendance" to add a class session
              </Typography>
            </Box>
          ) : selectedSessions.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedSessions.map(session => {
                const color = getColorForClass(session.class_name);
                const hasAbsent = session.absent_count > 0;
                const total = session.present_count + session.absent_count;
                return (
                  <Box
                    key={session.id}
                    onClick={() => navigate(`/attendance/${selectedDate}`)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1.5, borderRadius: 2.5, cursor: 'pointer',
                      border: '1px solid #EAECF0', bgcolor: 'white',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#D0D5DD', boxShadow: '0 2px 4px rgba(16,24,40,0.06)' }
                    }}
                  >
                    {/* Color bar */}
                    <Box sx={{
                      width: 4, alignSelf: 'stretch', borderRadius: 2,
                      bgcolor: hasAbsent ? '#F04438' : color.border,
                      flexShrink: 0,
                    }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#101828' }}>
                        {session.class_name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#667085' }}>
                          {total} student{total !== 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#12B76A' }} />
                          <Typography sx={{ fontSize: '0.8rem', color: '#027A48', fontWeight: 500 }}>
                            {session.present_count} present
                          </Typography>
                        </Box>
                        {hasAbsent && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F04438' }} />
                            <Typography sx={{ fontSize: '0.8rem', color: '#B42318', fontWeight: 500 }}>
                              {session.absent_count} absent
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <ChevronRight sx={{ color: '#D0D5DD', fontSize: 20 }} />
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Box>
      </Fade>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
